// Page d'analyse de cotation en temps réel
// Interface unique : saisie texte + résultat structuré NGAP

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { analyzeCotation, AnalyseInput, AnalyseResult, SoinIdentifie, CumulVerification, MajorationResult, LimiteVerification, Contradiction } from './engine';
import InputPanel from './components/InputPanel';
import ResultsPanel from './components/ResultsPanel';
import type { SoinInput, AnalysisMode } from './components/InputPanel';

function generateRapportNumber(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const seq = String(Math.floor(Math.random() * 9000) + 1000);
  return `${y}${m}${d}-${seq}`;
}

interface ExpertCotation {
  code: string;
  label: string;
  coefficient: number;
  passage: string;
  montant: number;
  justification: string;
  article: string;
}

interface ExpertCumul {
  acte1: string;
  acte2: string;
  compatible: boolean;
  regle: string;
  article: string;
}

interface ExpertMajoration {
  code: string;
  nom: string;
  applicable: boolean;
  montant: string;
  raison: string;
  article: string;
}

interface ExpertLimite {
  code: string;
  nom: string;
  limite: string;
  actuel: string;
  ok: boolean;
  article: string;
}

interface ExpertRisque {
  niveau: string;
  score: number;
  details: string[];
}

interface ExpertTotal {
  sousTotalActes: string;
  totalMajorations: string;
  totalGeneral: string;
}

interface ExpertResponse {
  cotations: ExpertCotation[];
  cumuls: ExpertCumul[];
  majorations: ExpertMajoration[];
  limites: ExpertLimite[];
  total: ExpertTotal;
  risque: ExpertRisque;
  avertissements: string[];
  message?: string;
  fallback?: boolean;
}

const CATEGORIE_MAP: Record<string, string> = {
  'AMI 1': 'Injections',
  'AMI 1,1': 'Pansements',
  'AMI 1,5': 'Prélèvements',
  'AMI 2': 'Pansements',
  'AMI 2,4': 'Injections',
  'AMI 2,8': 'Sondages',
  'AMI 3': 'Pansements',
  'AMI 3,05': 'Injections',
  'AMI 3,9': 'Surveillance',
  'AMI 4': 'Pansements',
  'AMI 4,2': 'Perfusions',
  'AMI 5,8': 'Surveillance',
  'AMI 6': 'Perfusions',
  'AMI 9': 'Perfusions',
  'AMI 10': 'Perfusions',
  'AMI 14': 'Perfusions',
  'AMI 15': 'Perfusions',
  'AMX': 'Injections',
  'TMI': 'Télésoin',
  'TMI 1,6': 'Télésoin',
  'TLS': 'Télésoin',
  'TLL': 'Télésoin',
  'TLD': 'Télésoin',
  'DI 1': 'Démarche de soins',
  'DI 1,2': 'Démarche de soins',
  'DI 2,5': 'Démarche de soins',
  'BSA': 'Forfait dépendance',
  'BSB': 'Forfait dépendance',
  'BSC': 'Forfait dépendance',
};

function mapExpertResponse(raw: ExpertResponse): AnalyseResult {
  const cotations: SoinIdentifie[] = raw.cotations.map((c, idx) => {
    const codeParts = c.code.split(' ');
    const categorie = CATEGORIE_MAP[c.code] || CATEGORIE_MAP[codeParts[0]] || 'Actes techniques';
    const baseAmount = c.coefficient > 0 ? Math.round((c.montant / c.coefficient) * 100) / 100 : 0;
    return {
      id: `${c.code}-${idx}-expert`,
      code: c.code,
      label: c.label,
      coefficient: c.coefficient,
      base: baseAmount.toFixed(2).replace('.', ',') + ' €',
      montant: c.montant,
      passage: c.passage || '—',
      categorie,
      justification: c.justification || '',
      article: c.article || '',
    };
  });

  const cumuls: CumulVerification[] = raw.cumuls.map((c) => ({
    acte1: c.acte1,
    acte2: c.acte2,
    compatible: c.compatible,
    regle: c.regle,
    article: c.article,
  }));

  const majorations: MajorationResult[] = raw.majorations.map((m) => ({
    code: m.code,
    nom: m.nom,
    applicable: m.applicable,
    montant: m.montant,
    raison: m.raison,
    article: m.article,
  }));

  const limites: LimiteVerification[] = raw.limites.map((l) => ({
    code: l.code,
    nom: l.nom,
    limite: l.limite,
    actuel: l.actuel,
    ok: l.ok,
    article: l.article,
  }));

  const score = raw.risque.score;
  const risqueCouleur = score >= 85 ? 'green' : score >= 65 ? 'amber' : 'red';

  const actesInjection = cotations.filter((c) => c.categorie === 'Injections').length;
  const actesPansement = cotations.filter((c) => c.categorie === 'Pansements').length;

  const nbPassages = new Set(cotations.map((c) => c.passage)).size;
  const categories = [...new Set(cotations.map((c) => c.categorie))];
  const resumeAnalyse = `${cotations.length} acte${cotations.length > 1 ? 's' : ''} identifié${cotations.length > 1 ? 's' : ''} — ${nbPassages} passage${nbPassages > 1 ? 's' : ''} — ${categories.length} catégorie${categories.length > 1 ? 's' : ''} de soins — Analyse GPT-4o`;

  const sources = [
    'Article 11B NGAP — Actes de soins infirmiers et pansements',
    'Article 12 NGAP — Démarche de soins infirmiers (BSI)',
    'Article 13B NGAP — Prélèvements biologiques',
    'Article 14 NGAP — Majorations dimanches et jours fériés',
    'Article 15 NGAP — Majorations de nuit',
    'Avenant n°6 — Convention nationale des IDEL (MAU, MCI, MIE)',
    'Avenant n°9 — Convention nationale IDEL — Télésoin (TMI, TLS, TLL, TLD)',
    '🤖 Analyse experte générée par GPT-4o avec les tarifs NGAP 2026',
  ];

  // Contradictions venant de GPT-4o ou fallback
  const contradictions: Contradiction[] = (raw as Record<string, unknown>).contradictions as Contradiction[] || [];

  return {
    cotations,
    cumuls,
    majorations,
    limites,
    total: {
      sousTotalActes: raw.total.sousTotalActes,
      totalMajorations: raw.total.totalMajorations,
      totalGeneral: raw.total.totalGeneral,
    },
    risque: {
      niveau: raw.risque.niveau,
      score: raw.risque.score,
      couleur: risqueCouleur,
      details: raw.risque.details,
    },
    sources,
    avertissements: raw.avertissements || [],
    resumeAnalyse,
    actesInjection,
    actesPansement,
    contradictions,
  };
}

export default function AnalysisPage() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalyseResult | null>(null);
  const [rapportNumber, setRapportNumber] = useState('');
  const [analysisCount, setAnalysisCount] = useState(0);
  const [error, setError] = useState('');
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('local');

  const handleAnalyze = useCallback(async (info: SoinInput) => {
    setLoading(true);
    setError('');

    try {
      if (analysisMode === 'expert') {
        const { data, error: fnError } = await supabase.functions.invoke<ExpertResponse>('analyze-cotation', {
          body: { description: info.description },
        });

        if (fnError) {
          throw new Error(fnError.message || 'Erreur lors de l\'appel à l\'Edge Function');
        }

        if (!data) {
          throw new Error('Aucune réponse de l\'Edge Function');
        }

        if (data.fallback) {
          throw new Error(data.message || 'Analyse experte indisponible. Vérifiez la configuration OpenAI.');
        }

        const mappedResult = mapExpertResponse(data);
        setResult(mappedResult);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 800));

        const input: AnalyseInput = {
          description: info.description,
        };

        const analysisResult = analyzeCotation(input);
        setResult(analysisResult);
      }

      setRapportNumber(generateRapportNumber());
      setAnalysisCount((prev) => prev + 1);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Une erreur est survenue lors de l\'analyse. Veuillez réessayer.';
      setError(msg);
      console.error('Analysis error:', err);
    } finally {
      setLoading(false);
    }
  }, [analysisMode]);

  const handleReset = useCallback(() => {
    setResult(null);
    setRapportNumber('');
    setError('');
  }, []);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top bar */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                title="Retour au tableau de bord"
              >
                <i className="ri-arrow-left-line text-foreground-600"></i>
              </button>
              <span className="text-lg font-bold font-heading text-primary-600">CotIdel.</span>
              <span className="hidden sm:inline text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded">
                Analyse de cotation
              </span>
              {analysisMode === 'expert' && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full font-medium">
                  <i className="ri-sparkling-line"></i>
                  GPT-4o
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="hidden sm:flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                <i className="ri-dashboard-line"></i>
                <span>Dashboard</span>
              </button>
              <span className="text-xs text-foreground-400 hidden md:inline">
                {profile?.first_name} {profile?.last_name}
              </span>
              <button
                onClick={handleLogout}
                className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer flex items-center gap-1"
                title="Déconnexion"
              >
                <i className="ri-logout-box-r-line"></i>
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-2">
            Analyse de cotation
          </h1>
          <p className="text-sm text-foreground-500 max-w-2xl">
            Décrivez les soins réalisés. CotIdel identifie les codes NGAP, vérifie les cumuls,
            applique les majorations et évalue le risque CPAM.
            {analysisMode === 'expert' && (
              <span className="text-accent-600 font-medium"> Mode expert GPT-4o activé — analyse approfondie.</span>
            )}
            <strong className="text-foreground-700"> Aucune donnée n&apos;est conservée.</strong>
          </p>
        </div>

        {/* Input panel */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-8">
          <InputPanel
            loading={loading}
            onSubmit={handleAnalyze}
            onReset={handleReset}
            hasResults={result !== null}
            analysisMode={analysisMode}
            onModeChange={setAnalysisMode}
            expertAvailable={true}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <i className="ri-error-warning-line text-red-500 text-lg mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="text-sm text-red-700">{error}</p>
              {error.includes('OpenAI') || error.includes('indisponible') ? (
                <button
                  onClick={() => setAnalysisMode('local')}
                  className="mt-2 text-xs text-red-600 underline hover:text-red-800 cursor-pointer"
                >
                  Réessayer avec l&apos;analyse locale
                </button>
              ) : null}
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-4">
            {analysisMode === 'expert' && (
              <div className="bg-accent-50 border border-accent-200/40 rounded-lg p-4 mb-2 flex items-center gap-3">
                <i className="ri-sparkling-line text-accent-500 text-lg animate-pulse"></i>
                <div>
                  <p className="text-sm font-medium text-accent-700">GPT-4o analyse votre situation clinique...</p>
                  <p className="text-xs text-accent-500 mt-0.5">Consultation des règles NGAP 2026 en cours</p>
                </div>
              </div>
            )}
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5 animate-pulse">
                <div className="h-3 bg-background-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-2 bg-background-100 rounded w-full"></div>
                  <div className="h-2 bg-background-100 rounded w-5/6"></div>
                  <div className="h-2 bg-background-100 rounded w-4/6"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <ResultsPanel result={result} rapportNumber={rapportNumber} />
        )}

        {/* Empty state */}
        {!result && !loading && !error && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
              <i className="ri-brain-line text-primary-500 text-2xl"></i>
            </div>
            <h3 className="text-base font-semibold text-foreground-700 mb-2">
              Prêt à analyser
            </h3>
            <p className="text-sm text-foreground-400 max-w-md mx-auto">
              Décrivez une situation clinique dans le champ ci-dessus et lancez l&apos;analyse.
              CotIdel vérifiera chaque règle NGAP pour vous garantir une cotation sans erreur.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-center">
                <i className="ri-file-search-line text-primary-500 text-xl mb-2 block"></i>
                <span className="text-xs text-foreground-600">Reconnaissance des actes</span>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-center">
                <i className="ri-shield-check-line text-accent-500 text-xl mb-2 block"></i>
                <span className="text-xs text-foreground-600">Vérification des règles</span>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 text-center">
                <i className="ri-file-download-line text-secondary-500 text-xl mb-2 block"></i>
                <span className="text-xs text-foreground-600">Rapport téléchargeable</span>
              </div>
            </div>
          </div>
        )}

        {/* Counter badge */}
        {analysisCount > 0 && (
          <div className="mt-8 text-center">
            <span className="text-xs text-foreground-400">
              {analysisCount} analyse{analysisCount > 1 ? 's' : ''} réalisée{analysisCount > 1 ? 's' : ''} cette session
              <span className="mx-2">·</span>
              <i className="ri-lock-line text-xs"></i> Aucune donnée conservée
            </span>
          </div>
        )}
      </main>
    </div>
  );
}